import {
  afterNextRender,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
} from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import {
  Animation,
  Gesture,
  GestureController,
  GestureDetail,
  IonTabs,
} from '@ionic/angular/standalone';
import {
  clamp,
  clearStuckPageTransitionStyles,
  createTabPagerTransition,
  isDesktopViewport,
  TAB_SLIDING_CLASS,
  TAB_SWITCH_DURATION_MS,
  TAB_SWIPE_RELEASE_DURATION_MAX_MS,
  tabPagerSwipeDecision,
} from 'src/app/utils/responsive-nav-animation';
import {
  initTabPagerPerformanceWatch,
  isTabPagerLiteMode,
} from 'src/app/utils/tab-pager-performance';
import {
  MAIN_TAB_IDS,
  mainTabLink,
  pathToTabId,
} from '../main-tabs/main-tabs.config';
import { MainTabsPreloadService } from '../main-tabs/main-tabs-preload.service';

type TabPagerDrag =
  | {
      kind: 'interactive';
      forward: boolean;
      toTab: string;
      width: number;
      lastToward: number;
      lastVelocity: number;
      animation: Animation;
      cleanup: () => void;
      pages: HTMLElement[];
    }
  | {
      kind: 'mounting';
      forward: boolean;
      toTab: string;
      width: number;
      lastToward: number;
      lastVelocity: number;
    }
  | {
      kind: 'deferred';
      forward: boolean;
      toTab: string;
      width: number;
    }
  | {
      kind: 'lite';
      forward: boolean;
      toTab: string;
      width: number;
      lastToward: number;
      lastVelocity: number;
      mounting?: boolean;
    };

/**
 * Native tab pager: tap still slides a full width; a horizontal drag follows
 * the finger like iOS swipe-to-go-back (progressStart/Step/End), then snaps
 * complete or cancel. Sibling tabs are mounted in the background so the first
 * visit can animate too. Ionic's tab outlet stays unanimated (duration 0).
 */
@Directive({
  selector: 'ion-tabs[reSlidingTabs]',
})
export class SlidingTabsComponent {
  private readonly tabs = inject(IonTabs, { self: true });
  private readonly elementRef: ElementRef<HTMLIonTabsElement> = inject(
    ElementRef<HTMLIonTabsElement>,
  );
  private readonly gestureCtrl = inject(GestureController);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tabsPreload = inject(MainTabsPreloadService);

  private tabSwipeGesture?: Gesture;
  private activeTransition?: {
    animation: Animation;
    cleanup: () => void;
    pages: HTMLElement[];
  };
  private drag: TabPagerDrag | null = null;
  private pagerAnimating = false;
  private pagerAnimatingTimer?: ReturnType<typeof setTimeout>;
  private currentPage?: HTMLElement;
  private currentIndex = 0;
  private transitionGen = 0;
  private skipNextPlay = false;
  private warming = false;
  private warmingTarget: string | null = null;
  private warmingStartTab: string | null = null;
  private warmingGen = 0;
  private preloadAborted = false;
  private preloadCover?: HTMLElement;
  private readonly warmingClass = 're-tabs-warming';
  private readonly anchorClass = 're-tab-anchor';

  constructor() {
    afterNextRender(() => {
      this.configureOutlet();
      this.initPerformanceWatch();
      this.initSwipe();
      this.schedulePreload();
    });
  }

  private initPerformanceWatch(): void {
    const dispose = initTabPagerPerformanceWatch(() => {
      this.stopAllPagerMotion();
    });
    this.destroyRef.onDestroy(dispose);
  }

  private stopAllPagerMotion(): void {
    const drag = this.drag;
    this.drag = null;
    if (drag?.kind === 'interactive') {
      drag.animation.stop();
      drag.cleanup();
    }
    this.stopActivePagerAnimation();
    this.releaseWarmingIfNeeded();
    this.clearAllSlidingPageStyles();
    this.endPagerAnimation();
  }

  private clearAllSlidingPageStyles(): void {
    clearStuckPageTransitionStyles(this.elementRef.nativeElement);
  }

  private releaseWarmingIfNeeded(): void {
    if (!this.warming) {
      return;
    }
    this.warmingGen += 1;
    this.preloadAborted = true;
    this.endWarming();
  }

  private clearPagerAnimationTimer(): void {
    if (this.pagerAnimatingTimer !== undefined) {
      clearTimeout(this.pagerAnimatingTimer);
      this.pagerAnimatingTimer = undefined;
    }
  }

  private beginPagerAnimation(maxMs: number): void {
    this.clearPagerAnimationTimer();
    this.pagerAnimating = true;
    this.pagerAnimatingTimer = setTimeout(() => {
      this.drag = null;
      this.stopActivePagerAnimation();
      this.clearAllSlidingPageStyles();
      this.releaseWarmingIfNeeded();
      this.endPagerAnimation();
    }, maxMs);
  }

  private endPagerAnimation(): void {
    this.clearPagerAnimationTimer();
    this.pagerAnimating = false;
  }

  private stopActivePagerAnimation(): void {
    const active = this.activeTransition;
    if (!active) {
      return;
    }
    active.animation.stop();
    active.cleanup();
    this.activeTransition = undefined;
    this.transitionGen += 1;
    this.clearAllSlidingPageStyles();
  }

  private usesAnimatedPager(): boolean {
    return this.canSwipe() && !isTabPagerLiteMode();
  }

  private configureOutlet(): void {
    const outlet = this.tabs.outlet;
    if (!outlet) {
      return;
    }

    outlet.animated = false;

    const activateSub = outlet.activateEvents.subscribe(() => {
      this.onActivate();
    });
    const stackSub = outlet.stackDidChange.subscribe(() => {
      if (this.warming) {
        this.restoreTabBar(this.warmingStartTab ?? this.pathTabId());
      }
    });
    this.destroyRef.onDestroy(() => {
      activateSub.unsubscribe();
      stackSub.unsubscribe();
    });
    this.onActivate();
  }

  private onActivate(): void {
    const page =
      this.tabs.outlet.activatedComponentRef?.location.nativeElement;
    if (!(page instanceof HTMLElement)) {
      return;
    }

    const index = this.tabIndexForPage(page);
    const activatedTab = this.tabButtons()[index]?.tab;
    const locationTab = this.pathTabId();

    if (this.warming && this.isWarmActivate(activatedTab, locationTab)) {
      this.restoreTabBar(this.warmingStartTab ?? locationTab);
      return;
    }

    if (this.warming) {
      this.warmingGen += 1;
      this.preloadAborted = true;
      this.endWarming();
    }

    if (this.skipNextPlay) {
      this.skipNextPlay = false;
      this.currentPage = page;
      this.currentIndex = index;
      return;
    }

    if (!this.currentPage) {
      this.currentPage = page;
      this.currentIndex = index;
      return;
    }

    if (page === this.currentPage || index === this.currentIndex) {
      return;
    }

    if (this.usesAnimatedPager()) {
      this.playTransition(this.currentPage, page, this.currentIndex < index);
    }
    this.currentPage = page;
    this.currentIndex = index;
  }

  private playTransition(
    from: HTMLElement,
    to: HTMLElement,
    forward: boolean,
  ): void {
    const start = () => {
      this.stopActivePagerAnimation();

      const next = createTabPagerTransition(from, to, forward);
      if (!next) {
        return;
      }

      const gen = ++this.transitionGen;
      this.activeTransition = next;
      this.beginPagerAnimation(TAB_SWITCH_DURATION_MS + 120);

      next.animation.onFinish(() => {
        if (this.transitionGen !== gen) {
          return;
        }
        next.cleanup();
        if (this.activeTransition === next) {
          this.activeTransition = undefined;
        }
        this.clearAllSlidingPageStyles();
        this.endPagerAnimation();
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          void next.animation.play();
        });
      });
    };

    if (to.offsetWidth === 0) {
      requestAnimationFrame(() => requestAnimationFrame(start));
      return;
    }
    start();
  }

  private tabButtons(): HTMLIonTabButtonElement[] {
    return Array.from(
      this.elementRef.nativeElement.querySelectorAll('ion-tab-button'),
    );
  }

  private tabIndexForPage(page: HTMLElement): number {
    const tag = page.tagName.toLowerCase();
    const index = this.tabButtons().findIndex((button) => {
      const id = button.tab?.toLowerCase();
      return !!id && (tag === `re-${id}` || tag.endsWith(`-${id}`));
    });
    return index >= 0 ? index : this.currentIndex;
  }

  private tabIds(): string[] {
    const fromButtons = this.tabButtons()
      .map((button) => button.tab)
      .filter((tab): tab is string => !!tab);
    return fromButtons.length > 0 ? fromButtons : [...MAIN_TAB_IDS];
  }

  private pathTabId(path = this.location.path()): string {
    return pathToTabId(path, this.tabIds());
  }

  private isWarmActivate(
    activatedTab: string | undefined,
    locationTab: string,
  ): boolean {
    if (!activatedTab) {
      return false;
    }
    const mountingHiddenTab =
      activatedTab === this.warmingTarget && locationTab !== activatedTab;
    const restoringStartTab =
      activatedTab === this.warmingStartTab && locationTab === this.warmingStartTab;
    return mountingHiddenTab || restoringStartTab;
  }

  private pageForTab(tab: string): HTMLElement | undefined {
    const id = tab.toLowerCase();
    const inner =
      this.elementRef.nativeElement.querySelector<HTMLElement>('.tabs-inner') ??
      this.elementRef.nativeElement;
    const byTag = inner.querySelector<HTMLElement>(`re-${id}`);
    if (byTag) {
      return byTag;
    }
    const pages = Array.from(inner.querySelectorAll<HTMLElement>('.ion-page'));
    return pages.find((page) => {
      const tag = page.tagName.toLowerCase();
      return tag === `re-${id}` || tag.endsWith(`-${id}`);
    });
  }

  private selectedIndex(): number {
    const selected = this.tabs.getSelected();
    const fromButtons = this.tabButtons().findIndex(
      (button) => button.tab === selected,
    );
    return fromButtons >= 0 ? fromButtons : this.currentIndex;
  }

  private restoreTabBar(tab = this.pathTabId()): void {
    const bar =
      this.elementRef.nativeElement.querySelector<HTMLIonTabBarElement>(
        'ion-tab-bar',
      );
    if (bar) {
      bar.selectedTab = tab;
    }
  }

  private schedulePreload(): void {
    if (typeof window === 'undefined') {
      this.tabsPreload.markReady();
      return;
    }
    if (!this.canSwipe()) {
      this.tabsPreload.markReady();
      return;
    }
    void this.waitForPaint().then(() => this.preloadMissingTabs());
  }

  private async preloadMissingTabs(): Promise<void> {
    const missing = this.tabButtons()
      .map((button) => button.tab)
      .filter((tab): tab is string => !!tab && !this.pageForTab(tab));
    if (missing.length === 0) {
      this.tabsPreload.markReady();
      return;
    }

    const startUrl = this.location.path() || this.router.url;
    const ownsSession = this.beginWarming();
    if (!ownsSession) {
      this.tabsPreload.markReady();
      return;
    }

    await this.waitForPaint();
    await this.waitForPaint();

    try {
      for (const tab of missing) {
        if (this.preloadAborted) {
          break;
        }
        this.warmingTarget = tab;
        await this.router.navigateByUrl(mainTabLink(tab), {
          skipLocationChange: true,
        });
        this.restoreTabBar(this.warmingStartTab ?? this.pathTabId(startUrl));
        await this.waitForPaint();
      }

      if (!this.preloadAborted) {
        this.warmingTarget = null;
        await this.router.navigateByUrl(startUrl, { skipLocationChange: true });
        this.restoreTabBar(this.warmingStartTab ?? this.pathTabId(startUrl));
        await this.waitForPaint();
        await this.waitForPaint();
      }
    } finally {
      if (ownsSession) {
        await this.waitForPaint();
        this.endWarming();
      }
      this.tabsPreload.markReady();
    }
  }

  private waitForPaint(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  private beginWarming(): boolean {
    if (this.warming) {
      return false;
    }
    if (!this.currentPage) {
      return false;
    }
    this.warming = true;
    this.warmingStartTab = this.pathTabId();
    this.elementRef.nativeElement.classList.add(this.warmingClass);
    this.currentPage.classList.add(this.anchorClass);
    this.hideOutletDuringWarm();
    this.pinVisibleTab(this.currentPage);
    this.freezeTabBar(this.warmingStartTab);
    return true;
  }

  private hideOutletDuringWarm(): void {
    const outlet =
      this.elementRef.nativeElement.querySelector<HTMLElement>(
        'ion-router-outlet',
      );
    outlet?.style.setProperty('visibility', 'hidden');
  }

  private showOutletAfterWarm(): void {
    const outlet =
      this.elementRef.nativeElement.querySelector<HTMLElement>(
        'ion-router-outlet',
      );
    outlet?.style.removeProperty('visibility');
  }

  /**
   * Freeze a live snapshot over `.tabs-inner` so Ionic can activate sibling
   * tabs underneath without a visible swap.
   */
  private pinVisibleTab(page: HTMLElement): void {
    this.unpinVisibleTab();
    const inner =
      this.elementRef.nativeElement.querySelector<HTMLElement>('.tabs-inner');
    if (!inner) {
      return;
    }

    const cover = document.createElement('div');
    cover.className = 're-tab-preload-cover';
    const clone = page.cloneNode(true) as HTMLElement;
    clone.removeAttribute('id');
    clone.classList.remove(
      'ion-page-hidden',
      'ion-page-invisible',
      this.anchorClass,
    );
    clone.setAttribute('aria-hidden', 'true');
    cover.appendChild(clone);
    inner.appendChild(cover);
    this.preloadCover = cover;
  }

  private unpinVisibleTab(): void {
    this.preloadCover?.remove();
    this.preloadCover = undefined;
  }

  private freezeTabBar(tab: string): void {
    for (const button of this.tabButtons()) {
      if (button.tab === tab) {
        button.setAttribute('data-re-anchor', '');
      } else {
        button.removeAttribute('data-re-anchor');
      }
    }
    this.restoreTabBar(tab);
  }

  private endWarming(): void {
    this.warming = false;
    this.warmingTarget = null;
    const startTab = this.warmingStartTab;
    this.warmingStartTab = null;
    this.elementRef.nativeElement.classList.remove(this.warmingClass);
    this.unpinVisibleTab();
    this.showOutletAfterWarm();
    this.elementRef.nativeElement
      .querySelectorAll(`.${this.anchorClass}`)
      .forEach((el) => el.classList.remove(this.anchorClass));
    for (const button of this.tabButtons()) {
      button.removeAttribute('data-re-anchor');
    }
    if (startTab) {
      this.restoreTabBar(startTab);
    }
  }

  private async ensureTabMounted(
    tab: string,
  ): Promise<HTMLElement | undefined> {
    const existing = this.pageForTab(tab);
    if (existing) {
      return existing;
    }

    const startUrl = this.location.path() || this.router.url;
    const startTab = this.warmingStartTab ?? this.pathTabId(startUrl);
    const ownsSession = this.beginWarming();
    if (ownsSession) {
      await this.waitForPaint();
    }
    const gen = ++this.warmingGen;
    this.warmingTarget = tab;
    this.warmingStartTab = startTab;

    try {
      await this.router.navigateByUrl(mainTabLink(tab), {
        skipLocationChange: true,
      });
      this.restoreTabBar(startTab);
      return this.pageForTab(tab);
    } finally {
      if (ownsSession && gen === this.warmingGen) {
        if (!this.preloadAborted) {
          await this.router.navigateByUrl(startUrl, { skipLocationChange: true });
          this.restoreTabBar(startTab);
          await this.waitForPaint();
        }
        this.endWarming();
      }
    }
  }

  private syncBrowserUrl(tab: string): void {
    const path = mainTabLink(tab);
    if (this.pathTabId() !== tab) {
      this.location.replaceState(path);
    }
    this.restoreTabBar(tab);
  }

  private activateTab(tab: string): Promise<boolean> {
    this.skipNextPlay = true;
    const nav = this.tabs.select(tab);
    if (nav) {
      return nav.then(
        (ok) => {
          this.syncBrowserUrl(tab);
          return ok !== false;
        },
        () => {
          this.syncBrowserUrl(tab);
          return false;
        },
      );
    }
    this.syncBrowserUrl(tab);
    return Promise.resolve(true);
  }

  private initSwipe(): void {
    const inner =
      this.elementRef.nativeElement.querySelector<HTMLElement>('.tabs-inner') ??
      this.elementRef.nativeElement;

    this.tabSwipeGesture = this.gestureCtrl.create({
      el: inner,
      gestureName: 'tab-pager',
      gesturePriority: 40,
      direction: 'x',
      threshold: 10,
      maxAngle: 25,
      disableScroll: false,
      canStart: (detail) => this.canStartSwipe(detail),
      onMove: (detail) => this.onSwipeMove(detail, inner),
      onEnd: (detail) => this.onSwipeEnd(detail),
    });
    this.tabSwipeGesture.enable(this.canSwipe());

    const onResize = () => this.tabSwipeGesture?.enable(this.canSwipe());
    window.addEventListener('resize', onResize);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('resize', onResize);
      this.tabSwipeGesture?.destroy();
      this.stopAllPagerMotion();
      this.endWarming();
    });
  }

  private canSwipe(): boolean {
    if (isDesktopViewport()) {
      return false;
    }
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private canStartSwipe(detail: GestureDetail): boolean {
    if (!this.canSwipe() || this.drag || this.warming || this.pagerAnimating) {
      return false;
    }
    const target = detail.event?.target;
    if (
      target instanceof Element &&
      target.closest('ion-item-sliding, swiper-container, [data-no-tab-swipe]')
    ) {
      return false;
    }
    return true;
  }

  private onSwipeMove(detail: GestureDetail, inner: HTMLElement): void {
    if (!this.drag) {
      if (Math.abs(detail.deltaX) < 2) {
        return;
      }
      this.beginDrag(detail, inner);
      if (!this.drag) {
        return;
      }
    }

    if (this.drag.kind === 'mounting' || this.drag.kind === 'lite') {
      this.drag.lastToward = this.drag.forward ? -detail.deltaX : detail.deltaX;
      this.drag.lastVelocity = this.drag.forward
        ? -detail.velocityX
        : detail.velocityX;
      return;
    }

    if (this.drag.kind !== 'interactive') {
      return;
    }

    const toward = this.drag.forward ? -detail.deltaX : detail.deltaX;
    this.drag.lastToward = toward;
    this.drag.lastVelocity = this.drag.forward
      ? -detail.velocityX
      : detail.velocityX;
    const step = clamp(0, toward / this.drag.width, 0.9999);
    this.drag.animation.progressStep(step);
  }

  private beginDrag(detail: GestureDetail, inner: HTMLElement): void {
    const forward = detail.deltaX < 0;
    const buttons = this.tabButtons();
    const index = this.selectedIndex();
    const toTab = buttons[index + (forward ? 1 : -1)]?.tab;
    if (!toTab) {
      return;
    }

    const width = inner.clientWidth || window.innerWidth;
    const fromPage =
      this.currentPage ?? this.pageForTab(this.tabs.getSelected() ?? '');
    const toPage = this.pageForTab(toTab);

    if (!fromPage) {
      return;
    }

    if (!toPage) {
      const toward = forward ? -detail.deltaX : detail.deltaX;
      if (isTabPagerLiteMode()) {
        this.drag = {
          kind: 'lite',
          forward,
          toTab,
          width,
          lastToward: toward,
          lastVelocity: forward ? -detail.velocityX : detail.velocityX,
          mounting: true,
        };
        return;
      }
      this.drag = {
        kind: 'mounting',
        forward,
        toTab,
        width,
        lastToward: toward,
        lastVelocity: forward ? -detail.velocityX : detail.velocityX,
      };
      void this.ensureTabMounted(toTab).then((mounted) => {
        this.promoteMountedDrag(mounted, fromPage);
      });
      return;
    }

    if (isTabPagerLiteMode()) {
      const toward = forward ? -detail.deltaX : detail.deltaX;
      this.drag = {
        kind: 'lite',
        forward,
        toTab,
        width,
        lastToward: toward,
        lastVelocity: forward ? -detail.velocityX : detail.velocityX,
      };
      return;
    }

    this.startInteractiveDrag(fromPage, toPage, forward, toTab, width);
  }

  private startInteractiveDrag(
    fromPage: HTMLElement,
    toPage: HTMLElement,
    forward: boolean,
    toTab: string,
    width: number,
    initialStep = 0,
  ): void {
    this.stopActivePagerAnimation();
    const next = createTabPagerTransition(fromPage, toPage, forward);
    if (!next) {
      return;
    }

    this.activeTransition = next;

    next.animation.progressStart(true, 0);
    if (initialStep > 0) {
      next.animation.progressStep(clamp(0, initialStep, 0.9999));
    }
    this.drag = {
      kind: 'interactive',
      forward,
      toTab,
      width,
      lastToward: initialStep * width,
      lastVelocity: 0,
      animation: next.animation,
      cleanup: next.cleanup,
      pages: next.pages,
    };
  }

  private promoteMountedDrag(
    mounted: HTMLElement | undefined,
    fromPage: HTMLElement,
  ): void {
    const drag = this.drag;
    if (drag?.kind !== 'mounting') {
      return;
    }
    if (!mounted) {
      this.drag = {
        kind: 'deferred',
        forward: drag.forward,
        toTab: drag.toTab,
        width: drag.width,
      };
      return;
    }

    this.startInteractiveDrag(
      fromPage,
      mounted,
      drag.forward,
      drag.toTab,
      drag.width,
      drag.lastToward / drag.width,
    );
  }

  private onSwipeEnd(detail: GestureDetail): void {
    const drag = this.drag;
    this.drag = null;
    if (!drag) {
      return;
    }

    const toward =
      drag.kind === 'interactive' ||
      drag.kind === 'mounting' ||
      drag.kind === 'lite'
        ? drag.lastToward
        : drag.forward
          ? -detail.deltaX
          : detail.deltaX;
    const velocity =
      drag.kind === 'interactive' ||
      drag.kind === 'mounting' ||
      drag.kind === 'lite'
        ? drag.lastVelocity
        : drag.forward
          ? -detail.velocityX
          : detail.velocityX;
    const decision = tabPagerSwipeDecision(toward, velocity, drag.width);

    if (drag.kind === 'lite') {
      if (!decision.shouldComplete) {
        this.releaseWarmingIfNeeded();
        return;
      }
      const complete = () => {
        void this.activateTab(drag.toTab);
      };
      if (drag.mounting) {
        this.preloadAborted = true;
        this.warmingGen += 1;
        void this.ensureTabMounted(drag.toTab).then((mounted) => {
          if (mounted) {
            complete();
          }
        });
        return;
      }
      complete();
      return;
    }

    if (drag.kind === 'deferred' || drag.kind === 'mounting') {
      if (decision.shouldComplete) {
        this.preloadAborted = true;
        this.warmingGen += 1;
        void this.activateTab(drag.toTab);
      } else {
        this.releaseWarmingIfNeeded();
      }
      return;
    }

    const gen = ++this.transitionGen;
    this.activeTransition = {
      animation: drag.animation,
      cleanup: drag.cleanup,
      pages: drag.pages,
    };
    this.beginPagerAnimation(
      Math.max(decision.duration, TAB_SWITCH_DURATION_MS) + 120,
    );
    drag.animation.easing(
      decision.shouldComplete
        ? 'cubic-bezier(0.32, 0.72, 0, 1)'
        : 'cubic-bezier(1, 0, 0.68, 0.28)',
    );
    drag.animation.onFinish(() => {
      if (this.transitionGen !== gen) {
        return;
      }
      const finish = () => {
        drag.cleanup();
        if (this.activeTransition?.animation === drag.animation) {
          this.activeTransition = undefined;
        }
        this.clearAllSlidingPageStyles();
        this.endPagerAnimation();
      };
      if (decision.shouldComplete) {
        void this.activateTab(drag.toTab).then(finish, finish);
        return;
      }
      finish();
    });
    drag.animation.progressEnd(
      decision.shouldComplete ? 1 : 0,
      decision.step,
      decision.duration,
    );
  }
}
